import { useContext, useState, Dispatch, SetStateAction, useEffect } from 'react'
import {
  WppAccordion,
  WppActionButton,
  WppButton,
  WppIconChevron,
  WppIconEdit,
  WppInlineMessage,
  WppTypography,
} from '@wppopen/components-library-react'
import { uuidv4 } from '@wppopen/components-library'
import ViewTaskPageContext from 'context/viewTaskPageContext/viewTaskPageContext'
import TextContainerList from './TextContainerList/TextContainerList'
import TextContainerParagraph from './TextContainerParagraph/TextContainerParagraph'
import { getFormattedDate, snakeCaseToSentenceCase } from 'utils'
import type { JobsDetailData } from '@contracts'
import type { ExtractElementType } from 'utils/utils.d'
import clsx from 'clsx'
import styles from './TextContainer.module.scss'
import AsyncTypography from 'components/AsyncTypography/AsyncTypography'

type SingleJobContentItem = ExtractElementType<NonNullable<JobsDetailData['content_items']>>

export interface TextContainerProps {
  className?: string
  content: SingleJobContentItem
  status: JobsDetailData['status']
  isInitialising?: boolean
}

const TextContainer = ({ className, content, status, isInitialising }: TextContainerProps) => {
  const { type: title, content: text, original_content, created_at, edits } = content
  const [isViewHistoryActive, setIsViewHistoryActive] = useState(false)
  const {
    isPageEditable,
    contentTypeBeingEdited: isCurrentlyEditing,
    setContentTypeBeingEdited: setIsCurrentlyEditing,
    setIsPageEditable,
    setIsModalOpen,
    setContentTypeContent,
  } = useContext(ViewTaskPageContext)

  const isDisabled = !!isCurrentlyEditing && title !== isCurrentlyEditing
  const isEditModeEnabled = !!isCurrentlyEditing && title === isCurrentlyEditing
  const [textContent, setTextContent] = useState<string | string[]>(text!)
  const isEdited = Boolean(edits?.length)
  const isEmptyContent = !Boolean(textContent) || (Array.isArray(textContent) && !textContent.length)
  const errorMessage = 'Please provide content before saving'

  useEffect(() => {
    if (status === 'ready_for_review') {
      setIsPageEditable(true)
      return
    }
    setIsPageEditable(false)
  }, [status])

  const formattedTitle = snakeCaseToSentenceCase(title!)
  const containerClasses = clsx(
    styles.text_container,
    isDisabled && isPageEditable && styles.text_container__disabled,
    isEditModeEnabled && styles['--editing'],
    className,
  )

  const handleEditClick = () => {
    if (!title || !text) return
    setIsCurrentlyEditing(title)
    setContentTypeContent(text)
  }

  const handleSaveClick = () => {
    setIsModalOpen(true)
    setContentTypeContent(textContent)
  }

  const handleCancelClick = () => {
    if (!text) return
    setTextContent(text)
    setIsCurrentlyEditing(null)
    setContentTypeContent(null)
  }

  const handleViewChangeHistoryClick = () => {
    setIsViewHistoryActive(!isViewHistoryActive)
  }

  return (
    <div className={containerClasses}>
      <div className={styles.section_header}>
        <AsyncTypography type="s-midi" dataTestId="title" isLoading={isInitialising} tag="h3">
          {formattedTitle}
        </AsyncTypography>

        {isEdited && edits && (
          <WppTypography tag="p" type="xs-midi" data-testid="last-edited">
            Last edited: {getFormattedDate(new Date(edits[edits.length - 1].timestamp || 'N/A'))}
          </WppTypography>
        )}
      </div>
      {Array.isArray(text) ? (
        <TextContainerList
          textContent={textContent as string[]}
          setTextContent={setTextContent as Dispatch<SetStateAction<string[]>>}
          isEditModeEnabled={isEditModeEnabled && isPageEditable}
        />
      ) : (
        <TextContainerParagraph
          title={title!}
          textContent={textContent as string}
          setTextContent={setTextContent as Dispatch<SetStateAction<string>>}
          isEditModeEnabled={isEditModeEnabled && isPageEditable}
        />
      )}
      <div className={styles.edit_row}>
        {!isCurrentlyEditing && isPageEditable && (
          <WppActionButton onClick={handleEditClick} data-testid="edit-button">
            <WppIconEdit slot="icon-start" />
            Edit
          </WppActionButton>
        )}

        {!isCurrentlyEditing && isEdited && (
          <WppActionButton onClick={handleViewChangeHistoryClick} data-testid="view-history-button">
            <WppIconChevron
              slot="icon-start"
              className={clsx(styles.icon, { [styles.icon__rotation]: isViewHistoryActive })}
            />
            View change history
          </WppActionButton>
        )}

        {isCurrentlyEditing === title && isPageEditable && (
          <>
            <WppButton onClick={handleSaveClick} disabled={isEmptyContent}>
              Save
            </WppButton>
            <WppButton variant="secondary" onClick={handleCancelClick}>
              Cancel
            </WppButton>
          </>
        )}
      </div>
      {isEmptyContent && isCurrentlyEditing && (
        <WppInlineMessage
          data-testid="region-error-message"
          size="s"
          message={errorMessage}
          type="error"
          showTooltipFrom={errorMessage.length}
          className={styles.error}
        />
      )}
      {isEdited && !isCurrentlyEditing && isViewHistoryActive && isViewHistoryActive && (
        <WppAccordion
          expanded={isViewHistoryActive}
          withDivider={false}
          className={styles.history_container}
          data-testid="history-section"
        >
          <div data-testid="history-section-data-container" className={styles.history_container_inner}>
            <div className={styles.history_container__header}>
              <WppTypography tag="p" type="s-strong">
                Original copy
              </WppTypography>
              <WppTypography tag="p" type="xs-midi">
                Created: {getFormattedDate(created_at)}
              </WppTypography>
            </div>

            {Array.isArray(original_content) ? (
              <TextContainerList
                textContent={original_content}
                setTextContent={setTextContent as Dispatch<SetStateAction<string[]>>}
                isEditModeEnabled={false}
              />
            ) : (
              <TextContainerParagraph
                title={title!}
                textContent={original_content!}
                setTextContent={setTextContent as Dispatch<SetStateAction<string>>}
                isEditModeEnabled={false}
              />
            )}

            <div className={styles.history_container__comments_container}>
              <WppTypography tag="p" type="s-strong">
                Comments ({edits?.length || 1})
              </WppTypography>

              <div className={styles.history_container__comments_inner}>
                {edits?.map(({ user_name, timestamp, reason }) => (
                  <div className={styles.history_container__comment} data-testid="comment-section" key={uuidv4()}>
                    <WppTypography tag="p" type="xs-midi">
                      {getFormattedDate(timestamp)} by {user_name || 'unknown user'}
                    </WppTypography>
                    <WppTypography tag="p" type="s-body">
                      {reason || 'No comment provided'}
                    </WppTypography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </WppAccordion>
      )}
    </div>
  )
}

export default TextContainer
